import { takeEvery, fork, put, ForkEffect, PutEffect, CallEffect } from 'redux-saga/effects'
import { SSO_LOGIN, SSO_HANDLE_CALLBACK } from '../../actions/SSOLoginActions/ssoLoginAction'
import { ssoLoginFail } from '../../actions/SSOLoginActions/ssoLoginAction'
import { loginObtainedToken } from '../../actions/loginAction'
import { addLocalStorageValue } from '../../services/localStorageService'
import {
  LOCAL_TOKEN_KEY,
  LOCAL_REFRESH_KEY,
  SSO_ACCESS_TOKEN_KEY,
  SSO_REFRESH_TOKEN_KEY,
} from '../../_constants/variables'
import { analyticsTracker } from '../../services/analytics/analyticsTrackerService'
import { GAEventNames } from '../../domain/models/commons/GoogleAnalytics'
import text from '../../_constants/text'
import { popCookie } from '../../utils/cookieUtils'

const SSO_URL = process.env.REACT_APP_SSO_REDIRECT_URL

/**
 * Handle SSO login initiation - redirect user to the SSO provider
 */
export function* ssoLogin(): Generator<PutEffect, void, unknown> {
  try {
    window.location.href = SSO_URL
  } catch (error) {
    console.error('SSO login error:', error)
    yield put(ssoLoginFail(text.UNEXPECTED_ERROR))
  }
}

/**
 * Handle SSO callback - processes JWT tokens from cookies set by backend
 * Stores tokens and dispatches loginObtainedToken action, similar to regular login flow
 */
export function* handleSSOCallback(): Generator<PutEffect | CallEffect, void, unknown> {
  try {
    const accessToken = popCookie(SSO_ACCESS_TOKEN_KEY)
    const refreshToken = popCookie(SSO_REFRESH_TOKEN_KEY)

    if (!accessToken || !refreshToken) {
      console.error('SSO callback error: missing tokens in cookies')
      yield put(ssoLoginFail(text.UNEXPECTED_ERROR))
      return
    }

    addLocalStorageValue(LOCAL_TOKEN_KEY, accessToken)
    addLocalStorageValue(LOCAL_REFRESH_KEY, refreshToken)

    // use existing parseLoginToken function to parse token and handle refresh
    const tokenData = { type: 'Bearer', token: accessToken, refreshToken: refreshToken }
    yield put(loginObtainedToken(tokenData))

    analyticsTracker().track(GAEventNames.LOGIN, {})
  } catch (error) {
    console.error('SSO callback error:', error)
    yield put(ssoLoginFail(text.UNEXPECTED_ERROR))
  }
}

function* watchSSOLogin(): Generator<ForkEffect<never>, void, unknown> {
  yield takeEvery(SSO_LOGIN, ssoLogin)
}

function* watchSSOCallback(): Generator<ForkEffect<never>, void, unknown> {
  yield takeEvery(SSO_HANDLE_CALLBACK, handleSSOCallback)
}

const ssoLoginSagas = [fork(watchSSOLogin), fork(watchSSOCallback)]

export default ssoLoginSagas
